import { timing, variantForLevel, } from './params';
const FIELD_SIZE = 280;
const ORB_MARGIN = 28;
/**
 * Génère N positions d'orbes spread + jitter dans le champ stimulus.
 * MVP : positions statiques durant tracking. Sprint 2+ : animation.
 */
function makePositions(nOrbs) {
    const positions = [];
    const cx = FIELD_SIZE / 2;
    const cy = FIELD_SIZE / 2;
    const radius = (FIELD_SIZE - ORB_MARGIN * 2) / 2;
    for (let i = 0; i < nOrbs; i += 1) {
        const angle = (i / nOrbs) * Math.PI * 2 - Math.PI / 2;
        const jitter = (Math.random() - 0.5) * 18;
        const r = radius + jitter;
        positions.push({
            idx: i,
            x: cx + Math.cos(angle) * r,
            y: cy + Math.sin(angle) * r,
        });
    }
    return positions;
}
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    // value (engine) = nombre brut de distracteurs ; on clampe sur les bornes config.
    const nDistractors = Math.max(1, Math.min(8, Math.round(value)));
    return {
        trialIdx,
        targetSpeed: variant.targetSpeed,
        nDistractors,
        trackingDurationMs: variant.trackingDurationMs,
    };
}
export function createTargetTrackerAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        trueIdx: 0,
        positions: [],
        highlightVisible: false,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    return {
        mount() {
            // Idempotent — rendu déclaratif côté React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                const nOrbs = stim.nDistractors + 1;
                const positions = makePositions(nOrbs);
                const trueIdx = Math.floor(Math.random() * nOrbs);
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    trueIdx,
                    positions,
                    highlightVisible: false,
                });
                timers.push(setTimeout(() => {
                    // Phase tracking : cible marquée durant highlightMs, puis fenêtre suivi.
                    update({ subPhase: 'tracking', highlightVisible: true });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        update({ highlightVisible: false });
                        timers.push(setTimeout(() => {
                            update({ subPhase: 'awaiting_response' });
                        }, Math.max(0, stim.trackingDurationMs - timing.highlightMs)));
                    }, timing.highlightMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(idx) {
            if (state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const correct = idx === state.trueIdx;
            const rt = Date.now() - trialStart;
            const resp = {
                pickedIdx: idx,
                trueIdx: state.trueIdx,
                correct,
                reactionTimeMs: rt,
            };
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
export const FIELD = FIELD_SIZE;
//# sourceMappingURL=adapter.js.map